'use client';

import Image from 'next/image';
import Link from 'next/link';
import { MapView } from '@paction-2.0/ui/client';
import type { ProviderMarker } from '@paction-2.0/ui';
import { CANADA_CENTER } from '@/features/puppy-search/mapCenters';
import styles from './promoSection.module.scss';

// Fallback marker at center of Canada
const DEFAULT_MARKERS: ProviderMarker[] = [
  { geo: { coordinates: [-106.3468, 56.1304] } }
];

export type PromoSectionProps = {
  providerMarkers?: ProviderMarker[];
};

export const PromoSection = ({ providerMarkers }: PromoSectionProps) => {
  const markers = providerMarkers?.length ? providerMarkers : DEFAULT_MARKERS;
  return (
    <section
      className={styles.promo}
      aria-labelledby="cost-calculator-promo-heading"
    >
      <h2 id="cost-calculator-promo-heading" className={styles.heading}>
        <Image
          src="/icons/cost-calculator-icon.svg"
          alt="Cost Calculator"
          width={48}
          height={48}
          className={styles.headingIcon}
        />
        Calculate the Cost of{' '}
        <span className={styles.headingLineBreak}>
          <br />
        </span>
        Owning a Dog
      </h2>

      <div className={styles.row}>
        <article
          className={styles.featured}
          aria-labelledby="cost-calculator-promo-featured-title"
        >
          <div className={styles.featuredBody}>
            <div className={styles.featuredBadge}>
              <span className={styles.featuredBadgeText}>Featured on</span>
              <Image
                src="https://paction-shared-resources.s3.ca-central-1.amazonaws.com/articlePhotos/2CEVYcF5Mg.webp"
                alt="money.ca"
                className={styles.featuredBadgeLogo}
                width={120}
                height={32}
              />
            </div>
            <h3
              id="cost-calculator-promo-featured-title"
              className={styles.featuredTitle}
            >
              Two-thirds of Canadian dog owners risk pet surrender by
              underestimating ongoing costs
            </h3>
            <p className={styles.featuredExcerpt}>
              According to a recent Paction study, two-thirds (66.7%) of
              Canadian dog owners failed to calculate the ongoing expenses
              associated with pet ownership before adopting their canine
              companions.
            </p>
            <div className={styles.featuredActions}>
              <Link
                href="https://money.ca/news/two-thirds-of-canadian-dog-owners-risk-pet-surrender"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.readButton}
              >
                Read
              </Link>
            </div>
          </div>
          <div className={styles.featuredPreview}>
            <Image
              src="https://paction-shared-resources.s3.ca-central-1.amazonaws.com/articlePhotos/8YHz2HE8vD.webp"
              alt="Article preview"
              className={styles.featuredPreviewImage}
              fill
              sizes="(max-width: 48em) 0px, (max-width: 768px) 100vw, 18rem"
            />
          </div>
        </article>

        <div className={styles.right}>
          <div className={styles.mapCard}>
            <div className={styles.mapImage}>
              <MapView
                mapSource="leaflet"
                zoom={3}
                disableInteraction
                radius={50000}
                providerMarkers={markers}
                center={CANADA_CENTER}
              />
            </div>
            <div className={styles.mapTitle}>
              Find Reputable Breeders Near You
            </div>
            <div className={styles.mapDescription}>
              Browse Reputable Breeders who meet Paction&apos;s Certification
              standards.
            </div>
            <div className={styles.mapActions}>
              <Link className={styles.mapButton} href="/puppies">
                Search for a Pup
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
